import { useEffect } from 'react';

import { Checkbox, InfiniteScroll, VerticalContainer, Wrapper } from '@american-autonomy/ag-ui';

import SelectableGridHeader from 'components/SelectableGrid/components/SelectableGridHeader';
import SelectableGroupedTableRow from 'components/SelectableGrid/components/SelectableGroupedTableRow';
import WithResize from 'components/WithResize';
import GridText from 'components/text/GridText';

import useGridTablesResizeObserver from 'hooks/useGridTablesResizeObserver';

import useData from './hooks/useData';

import { TEST_ID } from './constants';
import type { Identifiable, Props } from './types';

import styles from './styles.module.scss';

const SelectableGrid = <T extends Identifiable>({
    className = '',
    columns,
    columnsVariable,
    getGroupKey,
    getSummaryText,
    isLoading = false,
    onNext,
    onSelectionChange,
    renderRow,
    rows = [],
    testId = TEST_ID
}: Props<T>) => {
    const { containerRef, handleResize } = useGridTablesResizeObserver(columnsVariable);

    const {
        endingMessage,
        fetchNext,
        groups,
        hasNextPage,
        isAllIndeterminate,
        isAllSelected,
        isGroupIndeterminate,
        isGroupOpen,
        isGroupSelected,
        isRowSelected,
        selectedIds,
        toggleAll,
        toggleGroup,
        toggleGroupSelection,
        toggleRow
    } = useData({ getGroupKey, getSummaryText, isLoading, onNext, rows });

    useEffect(() => {
        onSelectionChange?.(selectedIds);
    }, [onSelectionChange, selectedIds]);

    return (
        <VerticalContainer
            className={`${className} ${styles.selectableGrid}`}
            ref={containerRef}
            testId={testId}>
            <SelectableGridHeader>
                <Checkbox
                    isChecked={isAllSelected}
                    isIndeterminate={isAllIndeterminate}
                    onChange={toggleAll}
                />

                {columns.map(({ header, isResizable }) =>
                    isResizable !== false ? (
                        <WithResize key={header} onResize={handleResize}>
                            <GridText text={header} />
                        </WithResize>
                    ) : (
                        <GridText key={header} text={header} />
                    )
                )}
            </SelectableGridHeader>

            <InfiniteScroll
                endingMessage={
                    <Wrapper className={styles.wrapper}>
                        <GridText text={endingMessage} />
                    </Wrapper>
                }
                className={styles.infiniteScroll}
                fetchNextPage={fetchNext}
                hasNextPage={hasNextPage}
                loader={<></>}
                nextPageToken={String(rows.length)}>
                {groups.map(({ items, key, summaryText }) => {
                    const isOpen = isGroupOpen(key);

                    return (
                        <Wrapper key={key}>
                            <SelectableGroupedTableRow
                                isIndeterminate={isGroupIndeterminate(items)}
                                isOpen={isOpen}
                                isSelected={isGroupSelected(items)}
                                label={key}
                                onSelect={() => toggleGroupSelection(items)}
                                onToggle={() => toggleGroup(key)}
                                summaryText={summaryText}
                            />

                            {isOpen &&
                                items.map(item =>
                                    renderRow(item, isRowSelected(item.id), () =>
                                        toggleRow(item.id)
                                    )
                                )}
                        </Wrapper>
                    );
                })}
            </InfiniteScroll>
        </VerticalContainer>
    );
};

export default SelectableGrid;
